import { ANNUAL_AUDIENCES, QUARTER_AUDIENCES, audienceLabel, coverageLines, quarterStates, QUARTERS, type Audience, type PlanEntitlements, type Quarter } from './plan-entitlements'

export const AUDIENCES: ReadonlyArray<{ value: Audience; label: string }> = [...QUARTER_AUDIENCES, ...ANNUAL_AUDIENCES].map((value) => ({
  value,
  label: audienceLabel(value),
}))

// Only small_group folders carry a quarter; the API ignores it for large_group_custom.
export const hasQuarterFolder = (audience: Audience) => audience === 'small_group'

// The API 400s a `state` on the other audiences (BulkFiles::Folder::STATE_BASED).
export const hasStateFolder = (audience: Audience) => audience !== 'medicare_advantage' && audience !== 'large_group_custom'

export interface BulkFilesParams {
  adapter: string
  audience: Audience
  year: string
  quarter?: Quarter
  state?: string
}

const currentQuarter = (now: Date): Quarter => QUARTERS[Math.floor(now.getMonth() / 3)]

// Years the set is licensed for, newest first, plus the one in the URL so a pasted link still works.
export function yearOptions(plans: PlanEntitlements | null | undefined, now: Date, selected?: string): string[] {
  const years = new Set(Object.keys(plans ?? {}).filter((y) => /^\d{4}$/.test(y)))
  if (years.size === 0) years.add(String(now.getFullYear()))
  if (selected) years.add(selected)
  return [...years].sort((a, b) => Number(b) - Number(a))
}

// Junk URL values fall back to defaults, so a request never carries a value the API would reject.
export function parseFilesParams(
  params: URLSearchParams,
  { adapters, plans, now }: { adapters: readonly string[]; plans: PlanEntitlements | null | undefined; now: Date }
): BulkFilesParams {
  const adapter = adapters.find((a) => a === params.get('adapter')) ?? adapters[0] ?? ''
  const audience = AUDIENCES.find((a) => a.value === params.get('audience'))?.value ?? 'small_group'
  const rawYear = params.get('year') ?? ''
  const year = /^\d{4}$/.test(rawYear) ? rawYear : yearOptions(plans, now)[0]
  const rawQuarter = params.get('quarter') ?? ''
  const quarter = QUARTERS.find((q) => q === rawQuarter) ?? currentQuarter(now)
  const state = hasStateFolder(audience) && /^[A-Z]{2}$/.test(params.get('state') ?? '') ? (params.get('state') as string) : undefined
  return {
    adapter,
    audience,
    year,
    ...(hasQuarterFolder(audience) ? { quarter } : {}),
    ...(state ? { state } : {}),
  }
}

// States licensed on any product line for the folder's period (the files themselves are medical only).
export function licensedStates(plans: PlanEntitlements | null | undefined, folder: BulkFilesParams): Set<string> {
  const period = folder.quarter ?? 'Annual'
  const lines = coverageLines(plans, folder.year).licensed.filter((l) => l.audience === folder.audience)
  return new Set(lines.flatMap((l) => quarterStates(plans, folder.year, l.productLine, folder.audience)[period] ?? []))
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const units = ['KB', 'MB', 'GB', 'TB']
  let value = bytes / 1024
  let i = 0
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024
    i++
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[i]}`
}
